/**
 * FleetHourlyStoreDrawer: one store's hours, opened from a store row on Reports >
 * Fleet Hourly without leaving the page.
 *
 * For today it reads the Sweet Spot Hourly route (`GET /v1/stores/omni-hourly?store_no=`,
 * the same numbers the store itself sees, refreshed every five minutes while open). That
 * route serves the store's own today only, so when the page is showing a past day the
 * drawer reads the store out of the fleet response it already holds instead.
 *
 * Side drawer on desktop, full screen on mobile: the same shape as ScorecardDayDetail
 * (`w-full max-w-md`, backdrop button, Escape to close), so no new drawer chrome.
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';
import { formatFleetHour, type FleetHourlyCell, type FleetHourlyStore } from '@ampersandbrands/ui';
import { formatDate } from '@/lib/dateUtils';
import { isNotAvailable } from '@/lib/useStores';
import { storeViewFromFleet, useStoreHourly, type StoreHourlyView } from '@/lib/useFleetHourly';
import { ErrorCard } from '../stores/storesShared';

function money(n: number | null, digits = 0): string {
  return n === null
    ? '--'
    : n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
}

function count(n: number | null): string {
  return n === null ? '--' : n.toLocaleString();
}

function pct(n: number | null): string {
  return n === null ? '--' : `${(n * 100).toFixed(0)}%`;
}

function change(n: number | null): string {
  return n === null ? '--' : `${n > 0 ? '+' : ''}${(n * 100).toFixed(1)}%`;
}

function changeTone(n: number | null): string {
  if (n === null) return 'text-amp-ink-muted dark:text-amp-dark-ink-muted';
  return n >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300';
}

function SummaryTile({ label, cell, fmt }: { label: string; cell: FleetHourlyCell; fmt: (n: number | null) => string }) {
  return (
    <div className="rounded-lg border border-amp-line px-3 py-2 dark:border-amp-dark-line">
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</p>
      <p className="text-lg font-semibold tabular-nums text-amp-ink dark:text-amp-dark-ink">{fmt(cell.ty)}</p>
      <p className={`text-xs tabular-nums ${changeTone(cell.comp_week)}`}>
        {cell.ly_week === null ? 'No last-year figure' : `${change(cell.comp_week)} vs ${fmt(cell.ly_week)}`}
      </p>
    </div>
  );
}

function HoursBody({ view }: { view: StoreHourlyView }) {
  const t = view.totals;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <SummaryTile label="Sales so far" cell={t.sales} fmt={(n) => money(n)} />
        <SummaryTile label="Transactions" cell={t.transactions} fmt={count} />
        <SummaryTile label="Traffic" cell={t.traffic} fmt={count} />
        <SummaryTile label="Conversion" cell={t.conversion} fmt={pct} />
      </div>
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Compared with the same weekday last year, over the same hours
        {view.data_through_hour !== null ? `, through ${formatFleetHour(view.data_through_hour)} store time` : ''}. Last
        year's whole day: {money(view.ly_full_day.sales)} from {count(view.ly_full_day.transactions)} transactions.
      </p>
      {view.hours.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No sales have been recorded for this day yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-amp-line dark:border-amp-dark-line">
          <table className="min-w-full text-sm">
            <thead className="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Hour</th>
                <th className="px-3 py-2 text-right font-medium">Sales</th>
                <th className="px-3 py-2 text-right font-medium">Last year</th>
                <th className="px-3 py-2 text-right font-medium">Change</th>
                <th className="px-3 py-2 text-right font-medium">Trans.</th>
                <th className="px-3 py-2 text-right font-medium">Traffic</th>
                <th className="px-3 py-2 text-right font-medium">Conv.</th>
                <th className="px-3 py-2 text-right font-medium">Avg sale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {view.hours.map((h) => (
                <tr key={h.hour}>
                  <td className="px-3 py-2 tabular-nums text-amp-ink dark:text-amp-dark-ink">{formatFleetHour(h.hour)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">{money(h.ty.sales)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    {money(h.ly.sales)}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${changeTone(h.comp_sales)}`}>{change(h.comp_sales)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">
                    {count(h.ty.transactions)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">{count(h.ty.traffic)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">{pct(h.ty.conversion)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">
                    {money(h.ty.dpt, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function FleetHourlyStoreDrawer({
  storeNo,
  storeName,
  /** The store as the fleet response holds it, used when the page is showing a past day. */
  fleetStore,
  fleetUpdatedAt,
  /** True when the page is showing each store's own today, which is the only day the store route serves. */
  isToday,
  onClose,
}: {
  storeNo: string;
  storeName: string;
  fleetStore: FleetHourlyStore | null;
  fleetUpdatedAt: string | null;
  isToday: boolean;
  onClose: () => void;
}) {
  const live = useStoreHourly(storeNo, isToday);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const view: StoreHourlyView | null = isToday ? live.view : fleetStore ? storeViewFromFleet(fleetStore, fleetUpdatedAt) : null;
  const isLoading = isToday && live.isLoading;
  const error = isToday ? live.error : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop: click outside closes. */}
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/30" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${storeName} hour by hour`}
        className="relative z-10 flex h-full w-full max-w-2xl flex-col bg-amp-white shadow-2 dark:bg-amp-dark-surface"
      >
        <div className="flex items-start justify-between border-b border-amp-line px-5 py-4 dark:border-amp-dark-line">
          <div className="min-w-0">
            <p className="mb-0.5 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
              {view?.business_date ? formatDate(view.business_date) : 'Hour by hour'}
              {view?.timezone ? ` · ${view.timezone.replace('_', ' ')}` : ''}
            </p>
            <h2 className="truncate text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">
              {storeNo} {storeName}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading this store's hours...</p>
          ) : error ? (
            <ErrorCard
              message={
                isNotAvailable(error)
                  ? 'The hourly view for a single store is not available yet.'
                  : error.message
              }
            />
          ) : view ? (
            <HoursBody view={view} />
          ) : (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No hours to show for this store.</p>
          )}
        </div>
      </div>
    </div>
  );
}
